import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS, PHONE_NO_LENGTH, INPUT_MAX_25, TEXTAREA_MAX_LENGTH, languageSpoken, EXPERTISE, INPUT_LENGTH_40, INPUT_LENGTH_50 } from "../../../../utils/Constants";
import { handleServerValidations, DT, globalLoader, blockInvalidChar } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";
import { useTranslation } from "react-i18next";
import { Details, Edit } from "../../services/psychic.services";
import { TagPicker, Tooltip, Whisper } from "rsuite";
import PhoneInput from "../../../../utils/PhoneInput";
import { COMMON_INPUT_VALIDATION, EMAIL_VALIDATION, PHONE_VALIDATION } from "../../../../utils/commonValidations";

const AstrologerEdit = (props) => {
    const navigate = useNavigate();
    const params = useParams();
    const { t } = useTranslation();
    const [showdefault, setShowDefault] = useState({});
    const [previewimage, setPreviewImage] = useState("");
    const [saveType, setSaveType] = useState("");
    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        {
            title: t("Astrologer"),
            url: "/admin/astrologer-management/astrologer/list/1",
        },
        { title: t("Edit"), url: "" },
    ];

    useEffect(() => {
        Details(params.id)
            .then((response) => {
                setShowDefault(response && response.data ? response.data : []);
                setPreviewImage(response?.data?.profile_image);
            })
            .catch((error) => {
                console.log("error=====>", error);
            });
    }, [params.id]);

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .trim()
            .max(INPUT_LENGTH_40, DT(t("max_length_error_dynamic"), [INPUT_LENGTH_40]))
            .matches(/^[a-zA-Z\s]+$/, "Name can only contain alphabets and spaces")
            .concat(COMMON_INPUT_VALIDATION)
            .required("Name is required"),

        email: Yup.string().when([], {
            is: () => showdefault?.login_with === "email",
            then: (schema) => schema.concat(EMAIL_VALIDATION).required("Email is required"),
            otherwise: (schema) => schema.notRequired().nullable(),
        }),

        phone_number: Yup.string().when([], {
            is: () => showdefault?.login_with === "phone_number",
            then: (schema) => schema.required("Phone number is required").matches(/^[6-9]\d{9}$/, "Phone number must be a valid 10-digit Indian number"),
            otherwise: (schema) => schema.notRequired().nullable(),
        }),

        expertise: Yup.array()
            .of(
                Yup.object({
                    // expertise_type: Yup.string()
                    //   .trim()
                    //   .required("Expertise type is required"),
                    price_per_minute: Yup.number()
                        .transform((value, originalValue) => {
                            // Treat empty string, null, or NaN as null
                            if (originalValue === "" || originalValue === null || isNaN(originalValue)) {
                                return null;
                            }
                            // Convert string to number if it is a number-string
                            if (typeof originalValue === "string") {
                                const parsed = Number(originalValue);
                                return isNaN(parsed) ? null : parsed;
                            }
                            return value;
                        })
                        .nullable()
                        .typeError("Price must be a number")
                        .integer("Price must be an integer")
                        .positive("Price must be a positive number")
                        .max(10000, "Price must not exceed 10,000"),
                })
            )
            .min(1, "At least one expertise is required"),

        online_puja: Yup.array().of(
            Yup.object()
                .shape({
                    puja_type: Yup.string()
                        .max(20, "Puja name must be at most 20 characters")
                        .matches(/^[\w\s'-]+$/, "Puja name must contain only valid characters")
                        .nullable(), // Can be empty
                    price_per_minute: Yup.number()
                        .typeError("Price must be a number")
                        .integer("Price must be an integer")
                        .positive("Price must be a positive number")
                        .max(10000, "Price must not exceed 10,000")
                        .nullable(), // Can be empty
                })
                .test("puja-fields-required-together", "Both Puja name and Price per minute must be provided together, or both left empty.", function (value) {
                    const { puja_type, price_per_minute } = value;

                    const nameFilled = !!puja_type && puja_type.trim() !== "";
                    const priceFilled = typeof price_per_minute === "number" && price_per_minute !== null && price_per_minute !== "";

                    const priceValid = typeof price_per_minute === "number" && price_per_minute > 0 && price_per_minute <= 10000 && Number.isInteger(price_per_minute);

                    // Both empty
                    if (!nameFilled && !priceFilled) return true;

                    // Both filled
                    if (nameFilled && priceFilled) return priceValid;

                    // One is filled and not the other
                    return false;
                })
        ),

        language_spoken: Yup.array()
            .of(Yup.string().required("Each language spoken must be a non-empty string"))
            .min(1, "At least one language must be provided")
            .required("Language spoken is required"),

        experience: Yup.number().typeError("Experience must be a number").min(0, "Experience cannot be negative").max(100, "Experience seems too high can not exceed 100").required("Experience is required"),
        commission: Yup.number().typeError("Commission must be a number").min(0, "Commission cannot be negative").max(100, "Commission cannot exceed 100").required("Commission is required"),
        rating: Yup.number().typeError("Rating must be a number").min(0, "Rating cannot be negative").max(5, "Rating cannot exceed 5"),

        profile_image: Yup.mixed()
            .required("Image is required")
            .nullable()
            .test("fileOrUrl", "supported_file_error", (value) => {
                if (!value) return true;
                if (typeof value === "string") return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
                return ["image/jpg", "image/jpeg", "image/png", "image/webp", "image/gif"].includes(value.type);
            })
            .test("fileSize", "Image size is too large (max 5MB)", (value) => {
                if (!value || typeof value === "string") return true;
                return value.size <= 5 * 1024 * 1024;
            }),
    });

    console.log("showdefault", showdefault);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: showdefault && showdefault.name ? showdefault.name : "",
            email: showdefault && showdefault.email ? showdefault.email : "",
            phone_number: showdefault && showdefault?.phone_number ? showdefault?.phone_number : "",
            expertise: showdefault && showdefault.expertise ? showdefault.expertise : [],
            // online_puja: showdefault && showdefault.online_puja ? showdefault.online_puja : [{ puja_type: "", price_per_minute: "" }],
            online_puja: (showdefault && Array.isArray(showdefault.online_puja) && showdefault.online_puja.length > 0)
                        ? showdefault.online_puja 
                        : [{ puja_type: "", price_per_minute: "" }],
            language_spoken: showdefault && showdefault.language_spoken ? showdefault.language_spoken : [],
            experience: showdefault && showdefault.experience ? showdefault.experience : "",
            commission: showdefault && showdefault.commission ? showdefault.commission : 0,
            rating: showdefault && showdefault.rating ? showdefault.rating : 0,
            profile_image: showdefault && showdefault.profile_image ? showdefault.profile_image : "",
        },
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            globalLoader(true);
            let formData = new FormData();
            formData.append("o_id", params.id);
            formData.append("name", values.name);
            formData.append("email", values.email);
            formData.append("phone_number", values.phone_number);
            formData.append("expertise", JSON.stringify(values.expertise));
            formData.append("experience", values.experience);
            formData.append("commission", values.commission);
            formData.append("rating", values.rating);
            formData.append("language_spoken", JSON.stringify(values.language_spoken));
            formData.append("online_puja", JSON.stringify(values.online_puja));
            formData.append("profile_image", values.profile_image);
            Edit(formData)
                .then((response) => {
                    setSubmitting(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        if (saveType !== "Save") {
                            navigate(`/admin/astrologer-management/astrologer/list/1`);
                        }
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                    globalLoader(false);
                })
                .catch((error) => {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                    console.log("error ====> ", error);
                });
        },
    });

    const handlePriceChange = (expertiseType, price) => {
        const parsedPrice = parseFloat(price); // Convert input to a number

        // Update Formik values to set the price for the expertise
        const updatedExpertise = formik.values.expertise.map((exp) => (exp.expertise_type === expertiseType ? { ...exp, price_per_minute: parsedPrice } : exp));

        formik.setFieldValue("expertise", updatedExpertise); // Update Formik state
    };

    const addPuja = () => {
        formik.setFieldValue("online_puja", [...formik.values.online_puja, { puja_type: "", price_per_minute: "" }]);
    };

    // Handle removing a puja by its index
    const removePuja = (index) => {
        const currentPujas = formik.values.online_puja;
        if (currentPujas.length > 1) {
            const updatedPujas = currentPujas.filter((_, i) => i !== index);
            formik.setFieldValue("online_puja", updatedPujas);
        } else {
            // Do nothing or reset the last one to empty if removal attempted
            formik.setFieldValue("online_puja", [{ puja_type: "", price_per_minute: "" }]);
        }
    };

    console.log("formik.values", formik.values);
    console.log("formik.errors", formik.errors);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <form onSubmit={formik.handleSubmit}>
                <div className="row row-sm">
                    <div className="col-lg-12 col-md-12 animation_fade">
                        <div className="card custom-card">
                            <div className="card-body">
                                <div>
                                    <h6 className="main-content-label mb-3">{t("Edit Astrologer")}</h6>
                                </div>
                                <div className="row row-sm">
                                    <div className="col-md-6 text-center form-group">
                                        <label htmlFor="name" className="text-left d-flex">
                                            {t("Name")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <input
                                            name="name"
                                            id="name"
                                            type="text"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.name}
                                            className="form-control"
                                            placeholder={t("Enter name")}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError shortCodes={[INPUT_MAX_25]} name="name" form={formik} />
                                        </span>
                                    </div>

                                    {showdefault?.login_with === "email" && (
                                        <div className="col-lg-6 text-center form-group">
                                            <label htmlFor="email" className="text-left d-flex">
                                                {t("label_email")}:<span className="requirestar">*</span>{" "}
                                            </label>
                                            <input
                                                name="email"
                                                id="email"
                                                type="email"
                                                disabled
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.email}
                                                className="form-control"
                                                placeholder={t("placeholder_email")}
                                            />
                                            <span className="text-danger d-flex text-left">
                                                <CustomError name="email" form={formik} />
                                            </span>
                                        </div>
                                    )}

                                    {showdefault?.login_with === "phone_number" && (
                                        <div className="col-md-6 text-center form-group">
                                            <label className="text-left d-flex" htmlFor="phone_number">
                                                {t("label_phone_number")}:<span className="requirestar">*</span>{" "}
                                            </label>
                                            {/* <PhoneInput
                                              name="phone_number"
                                              id="phone_number"
                                              disabled
                                              placeholder={t('placeholder_phone_number')}
                                              onChange={(e) => { formik.setFieldValue('phone_number', e?.phone_number) }}
                                              onBlur={formik.handleBlur}
                                              value={formik.values.phone_number}
                                              className="form-control"
                                            /> */}
                                            <input
                                                name="phone_number"
                                                id="phone_number"
                                                type="text"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.phone_number}
                                                className="form-control"
                                                placeholder={t("Enter phone number")}
                                            />
                                            <span className="text-danger d-flex text-left">
                                                <CustomError name="phone_number" form={formik} />
                                            </span>
                                        </div>
                                    )}
                                    <div className="col-md-6 text-center form-group">
                                        <label htmlFor="experience" className="text-left d-flex">
                                            {t("Experience (Year) ")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <div className="input-group">
                                            <input
                                                name="experience"
                                                id="experience"
                                                type="number"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.experience}
                                                className="form-control"
                                                placeholder={t("Enter experience")}
                                            />
                                            <span className="input-group-text">Year</span>
                                        </div>
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="experience" form={formik} />
                                        </span>
                                    </div>

                                    <div className="col-md-6 text-center form-group">
                                        <label htmlFor="commission" className="text-left d-flex">
                                            {t("Commission (%)")}:<span className="requirestar">*</span>{" "}
                                        </label>
                                        <div className="input-group">   
                                            <input
                                                name="commission"
                                                id="commission"
                                                type="number"
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                value={formik.values.commission}
                                                className="form-control"
                                                placeholder={t("Enter commission")}
                                                onKeyDown={blockInvalidChar}
                                            />
                                            <span className="input-group-text">%</span>
                                        </div>
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="commission" form={formik} />
                                        </span>
                                    </div>

                                    <div className="form-group col-lg-6">
                                        <label>
                                            Admin Rating 
                                            {formik.values.average_rating ? (
                                                <span className="ml-2 text-muted" style={{ fontSize: "14px" }}>
                                                    (User Avg Rating: {formik.values.average_rating})
                                                </span>
                                            ) : null}
                                            {/* <span className="requirestar">*</span> */}
                                        </label>

                                        <input 
                                            name="rating"
                                            type="number"
                                            step="0.1"
                                            value={formik.values.rating}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className="form-control"
                                            placeholder="Admin rating out of 5" 
                                        />

                                        <span className="text-danger">{formik.touched.rating && formik.errors.rating}</span>
                                    </div>

                                    <div className="row row-sm">
                                        <h6>{t("Expertise")}</h6>
                                        {formik.values.expertise.map((exp, index) => (
                                        <div key={exp.expertise_type || index} className="col-md-6 text-center form-group">
                                          <label className="text-left d-flex">
                                            {EXPERTISE[exp.expertise_type] || exp.expertise_type}
                                            <span className="requirestar">*</span>
                                          </label>
                                          <div className="input-group">
                                            <input
                                              type="number"
                                              name={`expertise[${index}].price_per_minute`}
                                              value={exp.price_per_minute || ""}
                                              onChange={(e) => handlePriceChange(exp.expertise_type, e.target.value)}
                                              className="form-control"
                                              placeholder={t("Enter price per minute")}
                                            />
                                          </div>
                                          {formik.errors.expertise &&
                                            formik.errors.expertise[index] &&
                                            formik.errors.expertise[index].price_per_minute && (
                                              <span className="text-danger d-flex text-left">
                                                {formik.errors.expertise[index].price_per_minute}
                                              </span>
                                            )}
                                        </div>
                                      ))}
                                    </div>

                                    <div className="row row-sm">
                                        <h6>Online Puja</h6>

                                        {/* Dynamically render pujas */}
                                        {formik.values.online_puja.map((puja, index) => {
                                        const pujaErrors = formik.errors.online_puja?.[index];
                                        const touchedPuja = formik.touched.online_puja?.[index];

                                        return (
                                          <div key={index} className="row row-sm">
                                            {/* Puja Type Input */}
                                            <div className="col-md-4 form-group">
                                              <label className="text-left d-flex">Puja Type:<span className="requirestar">*</span></label>
                                              <input
                                                type="text"
                                                name={`online_puja[${index}].puja_type`}
                                                value={puja.puja_type}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="form-control"
                                                placeholder="Enter puja type"
                                              />
                                              <span className="form-text text-danger d-inline-block" style={{ minHeight: "1em" }}>
                                                {touchedPuja?.puja_type && typeof pujaErrors === "object" && pujaErrors?.puja_type
                                                  ? pujaErrors.puja_type
                                                  : (typeof pujaErrors === "string" && touchedPuja?.puja_type
                                                      ? pujaErrors
                                                      : <>&nbsp;</>)}
                                              </span>
                                            </div>
                                            {/* Price per minute Input */}
                                            <div className="col-md-4 form-group">
                                              <label className="text-left d-flex">Price per Minute:<span className="requirestar">*</span></label>
                                              <input
                                                type="number"
                                                name={`online_puja[${index}].price_per_minute`}
                                                value={puja.price_per_minute}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="form-control"
                                                placeholder="Enter price per minute"
                                              />
                                              <span className="form-text text-danger d-inline-block" style={{ minHeight: "1em" }}>
                                                {touchedPuja?.price_per_minute && typeof pujaErrors === "object" && pujaErrors?.price_per_minute
                                                  ? pujaErrors.price_per_minute
                                                  : (typeof pujaErrors === "string" && touchedPuja?.price_per_minute
                                                      ? pujaErrors
                                                      : <>&nbsp;</>)}
                                              </span>
                                            </div>
                                            {/* Buttons Container */}
                                            <div className="col-md-3 d-flex align-items-center mt-2">
                                              <button type="button" className="btn btn-danger mr-2" onClick={() => removePuja(index)}>
                                                Remove
                                              </button>
                                              {index === formik.values.online_puja.length - 1 && (
                                                <button type="button" className="btn btn-success" onClick={addPuja}>
                                                  Add Puja
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}

                                    </div>

                                    {/* <div className="col-md-6 text-center form-group">
                                        <label htmlFor="language_spoken" className="text-left d-flex">
                                          {t("Language spoken")}:
                                          <span className="requirestar">*</span>{" "}
                                        </label>
                                        <div className=" select-down-arrow">
                                          <select
                                            name="language_spoken"
                                            id="language_spoken"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.language_spoken}
                                            className="form-control" >
                                            {(languageSpoken).map((value, i) => {
                                              return (
                                                <option key={i} value={value.code}>{t(`${value.name}`)}</option>
                                              )
                                            })}
                                          </select>
                                        </div>
                                        <span className="text-danger d-flex text-left">
                                          <CustomError name="language_spoken" form={formik} />
                                        </span>
                                      </div> */}

                                    <div className="col-lg-6 text-center form-group">
                                        <label htmlFor="restaurant_logo" className="text-left d-flex">
                                            {t("label_logo")}:{" "}
                                            <Whisper placement="top" controlId="control-id-hover" trigger="hover" speaker={<Tooltip>{t("image_support_tooltip")}</Tooltip>}>
                                                <span className="field-more-info mt-1 ms-1 cp">?</span>
                                            </Whisper>
                                        </label>
                                        <input
                                            className="form-control imgInput"
                                            id="profile_image"
                                            name="profile_image"
                                            accept="image/*"
                                            type="file"
                                            onChange={(event) => {
                                                formik.setFieldValue("profile_image", event.target.files[0] || "");
                                                event.target.files.length === 1 ? setPreviewImage(URL.createObjectURL(event.target.files[0])) : setPreviewImage("");
                                            }}
                                        />
                                        <span className="text-danger d-flex text-left">
                                            <CustomError name="profile_image" form={formik} />
                                        </span>
                                        {previewimage ? (
                                            <ul className="question-image-preview questions-ul">
                                                <li className="pr_img_box">
                                                    <img src={previewimage} style={{ height: "100px" }} alt={"profileImg"} />
                                                </li>
                                            </ul>
                                        ) : (
                                            ""
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <button
                                            onClick={() => {
                                                setSaveType("Save");
                                            }}
                                            className="btn btn-info mr-2"
                                            type="submit"
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_save")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSaveType("");
                                            }}
                                            className="btn btn-success mr-2"
                                            type="submit"
                                        >
                                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                            {t("btn_save_exit")}
                                        </button>
                                        <button className="btn ripple btn-secondary" type="button" onClick={() => navigate(-1)}>
                                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                            {t("btn_cancel")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default AstrologerEdit;
